
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { State, Getter, Mutation } from 'vuex-class';

import HttpRequest from '../../assets/utils/HttpRequest';
import Util from '../../assets/utils/Util';

import EditableTable from '../grids/EditableTable.vue';
import Action from '../../assets/interfaces/Action';

@Component({
  name: 'CardPopup',
  components: {
    EditableTable,
  },
})
export default class CardPopup extends Vue {
  // #region [ PROPS ]
  @Prop({default: (): Action[] => []})
  private readonly actions : Action[];

  @Prop({default: ''})
  private readonly color : string;

  @Prop({default: ''})
  private readonly cols?: number | string;

  @Prop({default: ''})
  private readonly contentColor : string;

  @Prop({default: ''})
  private readonly description: any;

  @Prop({default: ''})
  private readonly descriptionColor : string;

  @Prop({default: 150})
  private readonly dialogMaxWidth: number | string;

  @Prop({default: 700})
  private readonly dialogHeight: number | string;

  @Prop({default: false})
  private readonly disabled : boolean;

  @Prop({default: () => {}})
  private readonly filters: any;

  @Prop({default: (): any => []})
  private readonly headers: any[];

  @Prop()
  private readonly height: number;

  @Prop({default: ''})
  private readonly icon: string;

  @Prop({default: (): any => []})
  private readonly items: any[];

  @Prop({default: false})
  private readonly loading: boolean;

  @Prop({ default: undefined })
  private readonly maxWidth: number;

  @Prop({ default: undefined })
  private readonly minWidth: number;

  @Prop({ default: '' })
  private readonly popupTitle: string;

  @Prop({default: false})
  private readonly reloadAfterCreated: boolean;

  @Prop({default: ''})
  private readonly route: string;

  @Prop({default: false })
  private readonly isNotRouter: boolean;

  @Prop({default: undefined})
  private readonly rowStyle: string | ((row: any) => string);

  @Prop({default: true})
  private readonly showAnimationWhenLoading: boolean;

  @Prop({default: false})
  private readonly showPopup: boolean;

  @Prop({default: false})
  private readonly showReloadButton: boolean;

  @Prop({default: false})
  private readonly tilt: boolean;

  @Prop({required: true})
  private readonly title: string;

  @Prop({default: 0})
  private readonly triggerReload: number;

  @Prop({default: 220})
  private readonly width: number;

  @Prop({default: false})
  private readonly renderTabs: boolean;

  @Prop({default: (): any => []})
  private readonly tabsContent: any[];
  // #endregion

  // #region [ DATA ]
  private showDialog = false;

  private tableRows: any = [];

  private isLoading = false;

  private tab = 0;

  private tabsRows = [];

  // #endregion

  // #region [ COMPUTED ]
  private get cardDescription(): string {
    const descriptionIsFunction = typeof this.description === 'function';

    const description = descriptionIsFunction ?
      this.description(this.tableRows, this.tabsRows) : this.description;

    return description;
  }

  private get contentColorStyle() : any {
    const style = {
      color: this.contentColor,
    };

    return style;
  }

  private get descriptionColorStyle() : any {
    const color = this.descriptionColor || this.contentColor;

    const style = {
      color,
    };

    return style;
  }

  private get popupHeaderStyleString() : string {
    const styleString =
      `background-color: ${this.color} !important;
       color: ${this.contentColor} !important;`;

    return styleString;
  }

  private get internalDisabled() : boolean {
    const disabled = this.disabled || this.isLoading;

    return disabled;
  }

  private get showLoading() : boolean {
    const show = this.showAnimationWhenLoading ?
      this.isLoading :
      false;

    return show;
  }

  private get getPopupTitle(): string {
    const title = this.popupTitle || this.title;

    return title;
  }

  private internalWidth(): string {
    if (!this.cols) {
      return this.width.toString(10);
    }

    let cols = typeof this.cols === 'string' ?
      parseFloat(this.cols) :
      this.cols;

    const percentage = Math.floor(12 / cols * 100) + '%';

    return percentage;
  }

  private cardStyle(): any {
    // const style = {
    //   'border-radius': '10px',
    //   width: this.internalWidth,
    // };

    const style = `border-radius: 10px !important; width: ${this.internalWidth} !important;`;

    return style;
  }

  private get wrapStyle() {
    // const style = {
    //   maxWidth: this.maxWidth,
    //   minWidth: this.minWidth,
    //   height: this.height,
    // };

    const style = `
      max-width: ${this.maxWidth}px;
      min-width: ${this.minWidth}px;
      height: ${this.height}px;`;

    return style;
  }

  private get iconSize(): number {
    const breakpoint: string = (this as any).$vuetify.breakpoint.name;

    const sizeRelation = {
      xl: 40,
      lg: 40,
      md: 37,
      sm: 33,
      xs: 33,
    };

    const size = sizeRelation[breakpoint];

    return size;
  }

  private get currentTab(): any {
    return this.getCurrentTab();
  }

  private get currentTabsRows(): any {
    return this.tabsRows[this.currentTab];
  }
  // #endregion

  // #region [ WATCHERS ]
  @Watch('triggerReload')
  private triggerReloadOnChange() {
    this.reload();
  }

  @Watch('items')
  private itemsOnChange() {
    this.tableRows = Util.deepCopy(this.items);
  }

  @Watch('loading')
  private loadingOnChange() {
    this.isLoading = this.loading;
  }
  // #endregion

  // #region [ LIFECYCLE ]
  private created() {
    if(this.route && this.reloadAfterCreated) {
      this.reload();
    }
  }
  // #endregion

  // #region [ METHODS ]
  private cardOnClick() {
    super.$emit('cardOnClick')
    if (this.showPopup) {
      this.showDialog = true;
    }
  }

  private async reload() {
    if (this.renderTabs) {
      await this.reloadTabs();
      return;
    }
    await this.reloadCoventional();
  }

  private async reloadTabs() {
    if (!this.renderTabs) {
      return;
    }

    this.tabsContent.forEach(async (tab) => {
      if (!tab.route) {
        return;
      }
      await this.reloadTab(tab.order);
    });

    super.$emit('afterReloadAllTabs', this.tabsRows);
  }

  private async reloadCoventional() {
    if (Util.isEmptyOrBlank(this.route)) {
      return;
    }

    try {
      const requestFilters = {
        requestType: 'FilterData',
        ...this.filters
      };

      this.isLoading = true;

      if(this.isNotRouter) return;

      const resp = await HttpRequest.get(this.route, requestFilters, false);
      const rows = resp.data.dataset.data || resp.data.dataset[this.route];

      this.tableRows = Util.deepCopy(rows);

      super.$emit('afterReload', this.tableRows);
    } catch (err) {
      this.tableRows = Util.deepCopy([]);
      console.error(err);

      super.$emit('onReloadFail', err);
    } finally {
      this.isLoading = false;
    }
  }

  private async reloadTab(tabOrder: number) {
    const tab = this.findTabContentByOrder(tabOrder);
    if (Util.isEmptyOrBlank(tab)) {
      return;
    }

    try {
      const requestFilters = {
        requestType: 'FilterData',
        ...this.filters
      };

      this.isLoading = true;

      if(this.isNotRouter) return;

      const resp = await HttpRequest.get(tab.route, requestFilters, false);
      const rows = resp.data.dataset.data || resp.data.dataset[tab.route];

      this.tabsRows[tab.order] = Util.deepCopy(rows);

      if (!Util.isEmptyOrBlank(tab.afterReload)) {
        tab.afterReload(this.tabsRows[tab.order]);
      }
      super.$emit(`afterReloadTab-${tab.order}`, this.tabsRows[tab.order]);
      super.$emit('afterReload', []);
    } catch (err) {
      this.tabsRows[tab.order] = Util.deepCopy([]);
      console.error(err);

      super.$emit(`onReloadFail-${tab.order}`, err);
      super.$emit(`onReloadFail`, []);
    } finally {
      this.isLoading = false;
    }
  }

  private async reloadCurrentTab() {
    await this.reloadTab(this.tab);
  }

  private removeTilt() {
    this.setCardTilt(0, 0);
  }

  private setCardTilt(
    x: number,
    y: number,
  ) {
    const card: HTMLElement = (this.$refs.card as any).$el;

    card.style.transform = `rotateX(${y}deg) rotateY(${x}deg)`;
  }

  private wrapOnMouseMove(
    event: MouseEvent,
  ) {
    if (!this.tilt) {
      return;
    }

    const MAX_ANGLE = 5;
    const SLOTS_X = 2;
    const SLOTS_Y = 2;

    const wrap: HTMLElement = (this.$refs.wrap as any);
    const card: HTMLElement = (this.$refs.card as any).$el;

    const wrapBounding = wrap.getBoundingClientRect();

    const width = wrapBounding.width;
    const height = wrapBounding.height;

    const minX = wrapBounding.x;
    const maxX = minX + width;

    const minY = wrapBounding.y;
    const maxY = minY + height

    const mouseX = event.pageX;
    const mouseY = event.pageY;

    const containsMouse = this.contains(minX, maxX, minY, maxY, mouseX, mouseY);

    if (!containsMouse) {
      this.removeTilt();
      return;
    }

    let x = Math.max(Math.min(mouseX - minX, width), 0);
    let y = Math.max(Math.min(mouseY - minY, height), 0);

    (window as any).x = x;
    (window as any).y = y;

    const relativeX = x / width;
    const relativeY = y / height;

    const distanceX = this.distanceFromCenter(SLOTS_X, relativeX);
    const distanceY = this.distanceFromCenter(SLOTS_Y, relativeY);

    const angleX = this.getAngle(SLOTS_X, distanceX, MAX_ANGLE);
    const angleY = this.getAngle(SLOTS_Y, distanceY, MAX_ANGLE) * -1;

    const changedAngle = this.changedAngle(card, angleX, angleY);

    if (changedAngle) {
      this.setCardTilt(angleX, angleY);
    }
}

  private distanceFromCenter(
    slots: number,
    relativeAxis: number,
  ): number {
    const mouseSlot = Math.floor(slots * relativeAxis);
    const even = slots % 2 === 0;
    const center = (slots - 1) / 2;
    let distance = mouseSlot - center;

    if (even) {
      distance = distance > 0 ?
        Math.round(distance) :
        Math.floor(distance);
    }

    const maxDistance = Math.floor(slots / 2);
    const minDistance = maxDistance * -1;

    distance = Math.min(maxDistance, distance);
    distance = Math.max(minDistance, distance);

    return distance;
  }

  private getAngle(
    slots: number,
    distance: number,
    maxAngle: number,
  ): number {
    const middle = Math.floor(slots / 2);
    const angle = (distance / middle) * maxAngle;

    return angle;
  }

  private contains(
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    pointX: number,
    pointY: number,
  ): boolean {
    const xContains = pointX > minX && pointX < maxX;
    const yContains = pointY > minY && pointY < maxY;

    return xContains && yContains;
  }

  private changedAngle(
    card: HTMLElement,
    newAngleX: number,
    newAngleY: number,
  ): boolean {
    const oldAngleX = parseFloat(card.style.transform.charAt(8));
    const oldAngleY = parseFloat(card.style.transform.charAt(22));

    const changed =
      newAngleX !== oldAngleX ||
      newAngleY !== oldAngleY;

    return changed;
  }

  private getCurrentTab() {
    for (const tabIndex in this.tabsContent) {
      const currentTab = this.tabsContent[tabIndex];
      if (currentTab.tab === this.tab) {
        return currentTab;
      }
    }

    return {};
  }

  private getTabRows(tab: number, definedRows?: any): any[] {
    if (!Util.isEmptyOrBlank(definedRows)) {
      return definedRows;
    }
    return this.tabsRows[tab];
  }

  private findTabContentByOrder(order: number) {
    return this.tabsContent.find((t) => t.order == order);
  }
  // #endregion
};
